// WYSIWYG-KÉPFELTÖLTÉS — a szerkesztő „kép" gombjának háromlépéses folyamata.
//
// MIÉRT HÁROM LÉPÉS: a bájtok SOSEM mennek át a BFF-en (ugyanaz az elv, mint a ticket-csatolmánynál) —
//   1) `POST /editor-images/upload-url` → { uploadUrl, fileId }
//   2) `PUT uploadUrl` (KÖZVETLENÜL a tárba, a presigned aláírás a fal)
//   3) `POST /editor-images/:fileId/commit` → { url }   ← EZ kerül a HTML-be
//
// ⚠️ A HTML-BE A COMMIT STABIL URL-JE KERÜL, nem a presigned. Egy presigned GET-URL órák alatt lejár; a
// szerkesztő tartalma viszont TÁROLT HTML (ticket-leírás, komment, üzenet) — a beágyazott kép egy nap múlva
// 403-cal törne el, és a mentett tartalom már nem javítható.
//
// A validálás (kép-típus + 5 MB) ITT is megvan — nem a szerver helyett, hanem hogy a felhasználó AZONNAL
// visszajelzést kapjon; a megkerülhetetlen fal a BFF sémája és az Odin presigned-aláírása.

import { api } from '@/lib/api';
import { prepareEditorMedia } from '@/lib/editor-media-prepare';

// A TÍPUS-WHITELIST és a MÉRET-PLAFON a SZERZŐDÉSBŐL jön (`EDITOR_MEDIA_TYPES` / `editorMediaMaxBytes`) —
// nem saját lista. A szerver ugyanezt validálja; egy külön kliens-lista elcsúszásakor a felhasználó azt
// látja, hogy „elfogadta, aztán mégis hibázott". A plafon TÍPUS-FÜGGŐ: kép 5 MB, videó 25 MB.

export type EditorImageUploadError = 'type' | 'size' | 'failed';

export interface EditorImageUploadResult {
  ok: boolean;
  /** Siker esetén a HTML-be írható, STABIL URL. */
  url?: string;
  /** Hiba esetén a HIBA FAJTÁJA (a hívó fordítja — nyers üzenetet nem mutatunk). */
  error?: EditorImageUploadError;
}

/**
 * Egy kép feltöltése a szerkesztőből. SOHA nem dob: minden ág `EditorImageUploadResult`-ra képződik.
 *
 * Az `accountId` a kép CÉL-FIÓKJA, és OPCIONÁLIS: az Odin fájl-metaadata tenant-kötött, a
 * cél-ÜGYFELET pedig a BFF oldja fel — fiókkal a fiók ügyfelét, fiók NÉLKÜL a BELSŐ (MORGENS) ügyfél-rekordot.
 * Így a kép-gomb MINDEN felületen működik (a „mindenkinek" szóló körlevélnek nincs cél-ügyfele), és a
 * jegy-formon sem kell előbb fiókot választani.
 */
export async function uploadEditorImage(
  original: File,
  accountId?: string,
): Promise<EditorImageUploadResult> {
  // típus → (nagy fotónál kicsinyítés) → méret — egy közös szabály a két feltöltőnek
  const prepared = await prepareEditorMedia(original);
  if (!prepared.ok) return { ok: false, error: prepared.error };
  const { file, contentType } = prepared;

  try {
    const signed = await api.editorImageUploadUrl.mutation({
      body: {
        // A kulcs KIMARAD, ha nincs fiók-kontextus (a `undefined` így nem megy át `null`-ként a wire-en).
        ...(accountId !== undefined && accountId !== '' ? { accountId } : {}),
        filename: file.name,
        contentType,
        sizeBytes: file.size,
      },
    });
    if (signed.status !== 200) return { ok: false, error: 'failed' };

    // A presigned PUT a TÁRBA megy — a `Content-Type` egyeznie kell az aláírásban szereplővel.
    const put = await fetch(signed.body.uploadUrl, {
      method: 'PUT',
      headers: { 'content-type': contentType },
      body: file,
    });
    if (!put.ok) return { ok: false, error: 'failed' };

    const committed = await api.editorImageCommit.mutation({
      params: { fileId: signed.body.fileId },
      body: accountId !== undefined && accountId !== '' ? { accountId } : {},
    });
    if (committed.status !== 200) return { ok: false, error: 'failed' };
    return { ok: true, url: committed.body.url };
  } catch {
    // Hálózati/parse-hiba → generikus jelzés (nyers hiba SOHA nem megy a felhasználóhoz).
    return { ok: false, error: 'failed' };
  }
}
